package co.uk.paxes.pinpoint.web.ads

import co.uk.paxes.pinpoint.web.AIDifficulty
import kotlinx.browser.window
import kotlin.js.Date
import kotlin.js.JsExport
import kotlin.js.JsName

/**
 * Manages persistent and session-based AI difficulty unlocks.
 * 
 * Unlocks can be:
 * - Permanent: Stored in localStorage with 30-day expiration
 * - Session: Temporary, cleared on page refresh (used as fallback when ads fail)
 */
@JsExport
object UnlockStateManager {
    
    private const val STORAGE_KEY_PREFIX = "pinpoint_unlock_"
    private const val UNLOCK_DURATION_DAYS = 30
    private const val MS_PER_DAY = 86400000L // 24 * 60 * 60 * 1000
    
    // Session-only unlocks (cleared on page refresh)
    private val sessionUnlocks: MutableSet<AIDifficulty> = mutableSetOf()
    
    /**
     * Check if a difficulty is unlocked (permanent or session).
     * EASY is always unlocked.
     */
    @JsName("isUnlocked")
    fun isUnlocked(difficulty: AIDifficulty): Boolean {
        // EASY is always unlocked
        if (difficulty == AIDifficulty.EASY) {
            return true
        }
        
        // Check session unlocks first
        if (sessionUnlocks.contains(difficulty)) {
            console.log("✅ UnlockStateManager: ${difficulty.name} is unlocked (session)")
            return true
        }
        
        // Check permanent unlocks in localStorage
        val unlockData = loadUnlockData(difficulty)
        if (unlockData != null) {
            if (!isExpired(unlockData.expiresAt)) {
                console.log("✅ UnlockStateManager: ${difficulty.name} is unlocked (permanent, expires ${Date(unlockData.expiresAt)})")
                return true
            } else {
                console.log("⏰ UnlockStateManager: ${difficulty.name} unlock expired, removing")
                clearUnlock(difficulty)
            }
        }
        
        console.log("🔒 UnlockStateManager: ${difficulty.name} is locked")
        return false
    }
    
    /**
     * Unlock a difficulty permanently with expiration.
     * Stores to localStorage with timestamp.
     */
    @JsName("unlockPermanently")
    fun unlockPermanently(difficulty: AIDifficulty) {
        if (difficulty == AIDifficulty.EASY) {
            console.log("⚠️ UnlockStateManager: EASY is always unlocked, skipping")
            return
        }
        
        val now = Date.now().toLong()
        val expiresAt = now + (UNLOCK_DURATION_DAYS * MS_PER_DAY)
        
        val unlockData = UnlockData(
            unlocked = true,
            timestamp = now,
            expiresAt = expiresAt
        )
        
        saveUnlockData(difficulty, unlockData)
        
        // Also add to session unlocks for immediate availability
        sessionUnlocks.add(difficulty)
        
        console.log("🔓 UnlockStateManager: ${difficulty.name} unlocked permanently (expires in $UNLOCK_DURATION_DAYS days)")
    }
    
    /**
     * Unlock a difficulty for current session only.
     * Used as fallback when ads fail.
     */
    @JsName("unlockForSession")
    fun unlockForSession(difficulty: AIDifficulty) {
        if (difficulty == AIDifficulty.EASY) {
            console.log("⚠️ UnlockStateManager: EASY is always unlocked, skipping")
            return
        }
        
        sessionUnlocks.add(difficulty)
        console.log("🔓 UnlockStateManager: ${difficulty.name} unlocked for session only")
    }
    
    /**
     * Load unlocks from localStorage and validate expiration.
     * Called on game initialization.
     */
    @JsName("loadUnlocks")
    fun loadUnlocks() {
        console.log("📂 UnlockStateManager: Loading unlocks from localStorage")
        
        var loadedCount = 0
        var expiredCount = 0
        
        for (difficulty in AIDifficulty.values()) {
            if (difficulty == AIDifficulty.EASY) continue
            
            val unlockData = loadUnlockData(difficulty)
            if (unlockData != null) {
                if (!isExpired(unlockData.expiresAt)) {
                    loadedCount++
                    console.log("  ✅ ${difficulty.name}: unlocked (expires ${Date(unlockData.expiresAt)})")
                } else {
                    expiredCount++
                    clearUnlock(difficulty)
                    console.log("  ⏰ ${difficulty.name}: expired, removed")
                }
            }
        }
        
        console.log("📂 UnlockStateManager: Loaded $loadedCount unlocks, removed $expiredCount expired")
    }
    
    /**
     * Check if an unlock has expired.
     */
    private fun isExpired(expiresAt: Long): Boolean {
        return Date.now().toLong() >= expiresAt
    }
    
    /**
     * Clear all unlocks (for testing).
     */
    @JsName("clearAllUnlocks")
    fun clearAllUnlocks() {
        console.log("🗑️ UnlockStateManager: Clearing all unlocks")
        
        for (difficulty in AIDifficulty.values()) {
            if (difficulty == AIDifficulty.EASY) continue
            clearUnlock(difficulty)
        }
        
        sessionUnlocks.clear()
        console.log("✅ UnlockStateManager: All unlocks cleared")
    }
    
    /**
     * Load unlock data from storage.
     */
    private fun loadUnlockData(difficulty: AIDifficulty): UnlockData? {
        val key = STORAGE_KEY_PREFIX + difficulty.name.lowercase()
        val json = co.uk.paxes.pinpoint.web.PlayGamaStorageManager.getItem(key) ?: return null  // ✅ Updated: use PlayGamaStorageManager
        
        return try {
            val obj = JSON.parse<dynamic>(json)
            UnlockData(
                unlocked = obj.unlocked as Boolean,
                timestamp = (obj.timestamp as Number).toLong(),
                expiresAt = (obj.expiresAt as Number).toLong()
            )
        } catch (e: Exception) {
            console.error("❌ UnlockStateManager: Failed to parse unlock data for ${difficulty.name}: ${e.message}")
            null
        }
    }
    
    /**
     * Save unlock data to storage.
     */
    private fun saveUnlockData(difficulty: AIDifficulty, data: UnlockData) {
        val key = STORAGE_KEY_PREFIX + difficulty.name.lowercase()
        val obj = js("{}")
        obj.unlocked = data.unlocked
        obj.timestamp = data.timestamp
        obj.expiresAt = data.expiresAt
        val json = JSON.stringify(obj)
        
        try {
            co.uk.paxes.pinpoint.web.PlayGamaStorageManager.setItem(key, json)  // ✅ Updated: use PlayGamaStorageManager
        } catch (e: Exception) {
            console.error("❌ UnlockStateManager: Failed to save unlock data for ${difficulty.name}: ${e.message}")
        }
    }
    
    /**
     * Clear unlock data from storage.
     */
    private fun clearUnlock(difficulty: AIDifficulty) {
        val key = STORAGE_KEY_PREFIX + difficulty.name.lowercase()
        co.uk.paxes.pinpoint.web.PlayGamaStorageManager.removeItem(key)  // ✅ Updated: use PlayGamaStorageManager
    }
    
    /**
     * Data class for unlock information.
     */
    private data class UnlockData(
        val unlocked: Boolean,
        val timestamp: Long,
        val expiresAt: Long
    )
}
